import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Database, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function DataExportPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/data/counts`);
      setInfo(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger les statistiques.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    setExporting(format || 'simple');
    try {
      const r = await axios.get(`${API}/admin/data/export${format === 'extended' ? '?format=extended' : ''}`, { responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `puro-export-${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé.');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'export.");
    } finally { setExporting(false); }
  };

  return (
    <Card className="bg-white border-[#E9DDCC] rounded-2xl" data-testid="data-export-panel">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-[#C99A55]" strokeWidth={1.6} />
          <h3 className="font-heading text-lg text-[#493329]">Export des données</h3>
        </div>
        <p className="text-xs text-[#9A8B78] mb-5">
          Télécharge toutes les collections de la base dans un seul fichier JSON (lecture seule, aucune donnée modifiée).
          Sur le site en ligne, l'export porte sur la base de production.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Button onClick={() => doExport('simple')} disabled={!!exporting}
            className="bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-xl h-11" data-testid="export-data-btn">
            {exporting === 'simple' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Exporter toutes les données
          </Button>
          <Button onClick={() => doExport('extended')} disabled={!!exporting} variant="outline"
            className="rounded-xl h-11 border-[#C99A55] text-[#8B6914]" data-testid="export-data-extended-btn">
            {exporting === 'extended' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Export complet (sauvegarde)
          </Button>
          <Button onClick={load} disabled={loading} variant="outline"
            className="rounded-xl h-11 border-[#E9DDCC] text-[#7A6455]" data-testid="refresh-counts-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {info && (
            <span className="text-sm text-[#7A6455]" data-testid="export-total">
              <strong className="text-[#493329]">{info.total_documents}</strong> documents · {info.collections} collections
            </span>
          )}
        </div>

        {loading && !info ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div>
        ) : info && (
          <div className="border border-[#F0E8DA] rounded-xl divide-y divide-[#F0E8DA] max-h-72 overflow-auto" data-testid="counts-list">
            {Object.entries(info.collection_counts).map(([name, n]) => (
              <div key={name} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-[#6B5A3E] font-mono text-xs">{name}</span>
                <span className="text-[#493329] font-medium">{n}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
